import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
import { supabasePublic } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { Section } from '@/components/Section';

/**
 * ClientSetupPage — Form de configuration client après paiement.
 *
 * Affiché tant que client_onboarding.config_payload IS NULL pour le slug.
 * Sauvegarde directement dans Supabase via la clé anon (le client est auth
 * via Clerk JWT, le RLS Supabase autorise UPDATE WHERE auth.uid() matche).
 *
 * 5 questions essentielles pour que les agents (LIV-02 LinkedIn, PRD-04 reels,
 * PRD-05 newsletter, PRD-08 emails, LIV-03 follow-up) puissent personnaliser
 * leurs outputs sur l'ICP exact du client.
 */

const TONS = [
  { value: 'professional', label: 'Professionnel' },
  { value: 'warm', label: 'Chaleureux' },
  { value: 'expert', label: 'Expert technique' },
  { value: 'storytelling', label: 'Storytelling personnel' },
  { value: 'data_driven', label: 'Data-driven (chiffres + études)' },
];

const FREQUENCES = [
  { value: 'reels_2_per_week', label: '2 reels par semaine' },
  { value: 'newsletter_monthly', label: '1 newsletter par mois' },
  { value: 'linkedin_3_per_week', label: '3 posts LinkedIn par semaine' },
  { value: 'linkedin_messages_20_per_week', label: '20 messages prospection LinkedIn par semaine' },
  { value: 'email_followup_daily', label: 'Relances email automatiques' },
  { value: 'cr_session_after_each_call', label: 'CR session après chaque call client' },
];

interface ConfigPayload {
  client_icp: string;
  pillars: string[];
  ton: string;
  frequencies: string[];
  links: { linkedin: string; website: string };
  notes: string;
}

export function ClientSetupPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { getToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [clientIcp, setClientIcp] = useState('');
  const [pillars, setPillars] = useState<string[]>(['', '', '', '', '']);
  const [ton, setTon] = useState(TONS[0]!.value);
  const [frequencies, setFrequencies] = useState<string[]>([]);
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [niche, setNiche] = useState<string>('');

  // Charge la niche du client + pré-remplit si config existe déjà
  useEffect(() => {
    async function load() {
      if (!slug) {
        setError('Slug manquant');
        setLoading(false);
        return;
      }
      const sb = supabasePublic(() => getToken());
      if (!sb) {
        setError('Supabase non configuré');
        setLoading(false);
        return;
      }
      try {
        // Récupère le client par slug
        const { data: client, error: clientErr } = await sb
          .from('clients')
          .select('id, niche, metadata')
          .eq('slug', slug)
          .maybeSingle();
        if (clientErr) throw clientErr;
        if (!client) {
          setError(`Client "${slug}" introuvable`);
          setLoading(false);
          return;
        }
        const c = client as { id: string; niche: string | null; metadata: Record<string, unknown> | null };
        setNiche(c.niche ?? 'coach-business');

        // Récupère client_onboarding lié si existe (via metadata.client_onboarding_id)
        const onboardingId = (c.metadata ?? {})['client_onboarding_id'];
        if (typeof onboardingId === 'string') {
          const { data: ob } = await sb
            .from('client_onboarding')
            .select('config_payload')
            .eq('id', onboardingId)
            .maybeSingle();
          const cfg = ob && (ob as { config_payload: ConfigPayload | null }).config_payload;
          if (cfg) {
            setClientIcp(cfg.client_icp ?? '');
            setPillars(Array.isArray(cfg.pillars) && cfg.pillars.length === 5 ? cfg.pillars : ['', '', '', '', '']);
            setTon(cfg.ton ?? TONS[0]!.value);
            setFrequencies(Array.isArray(cfg.frequencies) ? cfg.frequencies : []);
            setLinkedinUrl(cfg.links?.linkedin ?? '');
            setWebsiteUrl(cfg.links?.website ?? '');
            setNotes(cfg.notes ?? '');
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [slug]);

  function setPillar(idx: number, value: string) {
    setPillars((prev) => prev.map((p, i) => (i === idx ? value : p)));
  }

  function toggleFrequency(value: string) {
    setFrequencies((prev) =>
      prev.includes(value) ? prev.filter((f) => f !== value) : [...prev, value],
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!slug) {
      setError('Slug manquant');
      setSubmitting(false);
      return;
    }

    // Validations
    if (clientIcp.trim().length < 30) {
      setError("Décris ton ICP en au moins 30 caractères (ex : 'DAF de PME industrielles 30-200 sal en France')");
      setSubmitting(false);
      return;
    }
    const filledPillars = pillars.map((p) => p.trim()).filter((p) => p.length > 0);
    if (filledPillars.length < 3) {
      setError('Renseigne au moins 3 piliers de contenu (5 idéalement)');
      setSubmitting(false);
      return;
    }

    const payload: ConfigPayload = {
      client_icp: clientIcp.trim(),
      pillars: filledPillars,
      ton,
      frequencies,
      links: { linkedin: linkedinUrl.trim(), website: websiteUrl.trim() },
      notes: notes.trim(),
    };

    const sb = supabasePublic(() => getToken());
    if (!sb) {
      setError('Supabase non configuré');
      setSubmitting(false);
      return;
    }

    try {
      // Récupère le client + son onboarding_id
      const { data: client } = await sb.from('clients').select('id, metadata').eq('slug', slug).maybeSingle();
      if (!client) throw new Error('Client introuvable');
      const c = client as { id: string; metadata: Record<string, unknown> | null };
      const onboardingId = (c.metadata ?? {})['client_onboarding_id'];

      if (typeof onboardingId !== 'string') {
        throw new Error("Aucun onboarding lié au client. Contacte Sebastian (sebastian@ducens.io).");
      }

      // UPDATE config_payload + status='config_received'
      const { error: updErr } = await sb
        .from('client_onboarding')
        .update({
          config_payload: payload,
          config_received_at: new Date().toISOString(),
          status: 'config_received',
        })
        .eq('id', onboardingId);

      if (updErr) throw updErr;

      setSuccess(true);
      setTimeout(() => navigate(`/clients/${slug}`), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de sauvegarde');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ducens-muted" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-ducens-bg flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-ducens-border rounded-xl p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-ducens-success mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">Configuration enregistrée</h1>
          <p className="text-sm text-ducens-muted">
            Tes agents vont commencer à produire leurs premiers outputs sous 24h. Tu seras notifié dès qu'ils seront prêts à valider.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ducens-bg">
      <header className="border-b border-ducens-border bg-white sticky top-0 z-30">
        <div className="px-6 py-3 max-w-3xl mx-auto">
          <div className="font-semibold text-sm">Configure tes agents IA</div>
          <div className="text-xs text-ducens-muted">5 questions pour que tes agents produisent du contenu calibré sur ta niche</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 p-3 bg-ducens-warn/10 border border-ducens-warn rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-ducens-warn shrink-0 mt-0.5" />
            <div className="text-sm text-ducens-warn">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 bg-white border border-ducens-border rounded-lg p-6">
          {niche && (
            <div className="text-xs text-ducens-muted">
              Niche détectée par le quiz : <code className="px-1 py-0.5 bg-ducens-bg rounded">{niche}</code>
              {' '}— les agents s'y adapteront automatiquement.
            </div>
          )}

          <Section
            title="1. Ton ICP exact"
            description="Décris précisément qui tu vises (titre + secteur + taille + zone géo). Plus c'est spécifique, mieux les messages LinkedIn et la newsletter convertiront."
          >
            <textarea
              required
              rows={3}
              value={clientIcp}
              onChange={(e) => setClientIcp(e.target.value)}
              placeholder="Ex : DAF/DG de PME industrielles ou retail B2B, 30-200 salariés, en France, qui font de la transformation digitale ou cherchent à industrialiser leur supply chain."
              className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 focus:outline-none focus:border-ducens-ink"
            />
          </Section>

          <Section
            title="2. Tes 5 piliers de contenu"
            description="Les thèmes sur lesquels tu veux être vu comme l'expert (3 minimum, 5 idéalement)."
          >
            {pillars.map((p, i) => (
              <input
                key={i}
                type="text"
                value={p}
                onChange={(e) => setPillar(i, e.target.value)}
                placeholder={`Pilier ${i + 1} (ex : ${
                  ['lean management', 'KPI ops', 'transformation digitale', 'supply chain', 'change management'][i]
                })`}
                className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 mb-2 focus:outline-none focus:border-ducens-ink"
              />
            ))}
          </Section>

          <Section
            title="3. Ton préféré"
            description="Comment tu veux que les agents écrivent en ton nom."
          >
            <div className="grid grid-cols-2 gap-2">
              {TONS.map((t) => (
                <label
                  key={t.value}
                  className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer text-sm ${
                    ton === t.value
                      ? 'border-ducens-ink bg-ducens-accent/30'
                      : 'border-ducens-border hover:border-ducens-muted'
                  }`}
                >
                  <input
                    type="radio"
                    name="ton"
                    value={t.value}
                    checked={ton === t.value}
                    onChange={() => setTon(t.value)}
                    className="w-3 h-3"
                  />
                  {t.label}
                </label>
              ))}
            </div>
          </Section>

          <Section
            title="4. Fréquence éditoriale souhaitée"
            description="Coche ce que tu veux que les agents produisent automatiquement (tu valideras chaque output)."
          >
            <div className="space-y-2">
              {FREQUENCES.map((f) => (
                <label
                  key={f.value}
                  className="flex items-start gap-2 p-2 border border-ducens-border rounded-md cursor-pointer text-sm hover:bg-ducens-bg"
                >
                  <input
                    type="checkbox"
                    checked={frequencies.includes(f.value)}
                    onChange={() => toggleFrequency(f.value)}
                    className="w-4 h-4 mt-0.5"
                  />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
          </Section>

          <Section
            title="5. Liens utiles"
            description="Pour que les agents s'inspirent de ton style existant."
          >
            <input
              type="url"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://www.linkedin.com/in/ton-profil"
              className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 mb-2 focus:outline-none focus:border-ducens-ink"
            />
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://ton-site.fr (optionnel)"
              className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 focus:outline-none focus:border-ducens-ink"
            />
          </Section>

          <Section title="Notes complémentaires (optionnel)" description="Tout ce que tu veux qu'on sache de plus.">
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex : éviter les mots 'révolutionnaire' et 'game changer', toujours signer Marie et pas Marie Dupont, etc."
              className="w-full text-sm border border-ducens-border rounded-md px-3 py-2 focus:outline-none focus:border-ducens-ink"
            />
          </Section>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-ducens-ink text-white py-3 rounded-md font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {submitting ? 'Enregistrement…' : 'Activer mes agents IA'}
          </button>
        </form>
      </main>
    </div>
  );
}
